'use client';

import { Fragment, PropsWithChildren, useContext } from 'react';
import { twMerge } from 'tailwind-merge';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { RichText } from '@lamesarv-sdk/components';
import { CommonFormContext } from '@/components/CommonForm';
import { CommonCheckbox } from '@/components/CommonForm/CommonCheckbox';
import { CommonInput } from '@/components/CommonForm/CommonInput';
import { CommonMaskInput } from '@/components/CommonForm/CommonMaskInput';
import { CommonTextarea } from '@/components/CommonForm/CommonTextarea';
import { CommonLocationSelect } from '@/components/CommonForm/CommonLocationSelect';
import { CommonSelect } from '@/components/CommonForm/CommonSelect';

import { DynamicsDealershipContactType, IDynamicsOptionSet } from '@/types/dynamicsForms';
import { IComponentGenericInquiryField, IComponentGenericInquiryForm } from '@/types/cms';

import { PropsWithClasses } from '@lamesarv-sdk/types';
import { IGenericInquiryFormProps } from '../types';

import { checkBoxSubscribe, checkboxTerms, messageError, messageSuccess } from '../constants';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

// --

export interface IBaseSubmitProps extends IComponentGenericInquiryForm, PropsWithClasses {
  styleClasses?: string;
  extraClasses?: string;
}

export const BaseSubmit = (props: Partial<IBaseSubmitProps>) => {
  const { isDisabled } = useContext(CommonFormContext);

  return (
    <button
      type="submit"
      disabled={isDisabled}
      className={twMerge(props.styleClasses, props.classes_submit, props.extraClasses)}
      data-tag-group={props.tag ? `form-inquiry-${props.tag}` : 'form-form-inquiry'}
      data-tag={props.tag ? `form-inquiry-${props.tag}-submit` : 'form-inquiry-submit'}
    >
      {props.submit_button_text || 'Submit'}
    </button>
  );
};

// --

export interface IBaseFieldsStylesProps {
  container?: string;
  label?: string;
  labelWithValue?: string;
  input?: string;
  inputWithValue?: string;
}

export interface IBaseFieldProps extends IComponentGenericInquiryField, PropsWithChildren {
  styleClasses?: IBaseFieldsStylesProps;
  inputClasses?: { [input: string]: IBaseFieldsStylesProps };
  wrapperClasses?: string;
  extraClasses?: string;
  locationsOptionSet?: IDynamicsOptionSet;
  productsListBase?: string;
}

export const BaseField = (props: IBaseFieldProps) => {
  const options = (props?.productsListBase || '')
    .split(/\r?\n|\r/)
    .filter(Boolean)
    .map((item) => ({
      title: item,
      value: item,
    }));

  let FormField: JSX.Element;

  switch (props.type) {
    case 'first-name':
      FormField = (
        <CommonInput
          label="First Name"
          name="firstName"
          isRequired={props.required}
          classContainer={twMerge(
            props.styleClasses.container,
            props.inputClasses?.firstName?.container,
            props.classes,
          )}
          classLabel={twMerge(props.styleClasses.label, props.inputClasses?.firstName?.label, props.classes_label)}
          classLabelWithValue={twMerge(
            props.styleClasses.labelWithValue,
            props.inputClasses?.firstName?.labelWithValue,
          )}
          classInput={twMerge(props.styleClasses.input, props.classes_input, props.inputClasses?.firstName?.input)}
          classInputWithValue={twMerge(
            props.styleClasses.inputWithValue,
            props.inputClasses?.firstName?.inputWithValue,
          )}
        />
      );
      break;
    case 'last-name':
      FormField = (
        <CommonInput
          label="Last Name"
          name="lastName"
          isRequired={props.required}
          classContainer={twMerge(props.styleClasses.container, props.inputClasses?.lastName?.container, props.classes)}
          classLabel={twMerge(props.styleClasses.label, props.inputClasses?.lastName?.label, props.classes_label)}
          classLabelWithValue={twMerge(props.styleClasses.labelWithValue, props.inputClasses?.lastName?.labelWithValue)}
          classInput={twMerge(props.styleClasses.input, props.classes_input, props.inputClasses?.lastName?.input)}
          classInputWithValue={twMerge(props.styleClasses.inputWithValue, props.inputClasses?.lastName?.inputWithValue)}
        />
      );
      break;
    case 'email':
      FormField = (
        <CommonInput
          label="Email"
          name="email"
          isRequired={props.required}
          classContainer={twMerge(props.styleClasses.container, props.inputClasses?.email?.container, props.classes)}
          classLabel={twMerge(props.styleClasses.label, props.inputClasses?.email?.label, props.classes_label)}
          classLabelWithValue={twMerge(
            props.styleClasses.labelWithValue,
            props.inputClasses?.email?.labelWithValue,
            props.classes_label,
          )}
          classInput={twMerge(props.styleClasses.input, props.classes_input, props.inputClasses?.email?.input)}
          classInputWithValue={twMerge(
            props.styleClasses.inputWithValue,
            props.inputClasses?.email?.inputWithValue,
            props.classes_input,
          )}
          type="email"
        />
      );
      break;
    case 'phone':
      FormField = (
        <CommonMaskInput
          mask="(999) 999-9999"
          label="Phone Number"
          name="phone"
          isRequired={props.required}
          cleanValueCallback={(value) => value.replace(/\D/g, '')}
          classContainer={twMerge(props.styleClasses.container, props.inputClasses?.phone?.container, props.classes)}
          classLabel={twMerge(props.styleClasses.label, props.inputClasses?.phone?.label, props.classes_label)}
          classLabelWithValue={twMerge(
            props.styleClasses.labelWithValue,
            props.inputClasses?.phone?.labelWithValue,
            props.classes_label,
          )}
          classInput={twMerge(props.styleClasses.input, props.classes_input, props.inputClasses?.phone?.input)}
          classInputWithValue={twMerge(
            props.styleClasses.inputWithValue,
            props.inputClasses?.phone?.inputWithValue,
            props.classes_input,
          )}
        />
      );
      break;
    case 'preferred-location':
      FormField = (
        <CommonLocationSelect
          locationsOptionSet={props.locationsOptionSet}
          label="Preferred Location"
          placeholder=""
          isRequired={props.required}
          classContainer={twMerge(props.styleClasses.container, props.inputClasses?.location?.container, props.classes)}
          classLabel={twMerge(props.styleClasses.label, props.inputClasses?.location?.label, props.classes_label)}
          classLabelWithValue={twMerge(
            props.styleClasses.labelWithValue,
            props.inputClasses?.location?.labelWithValue,
            props.classes_label,
          )}
          classInput={twMerge(props.styleClasses.input, props.classes_input, props.inputClasses?.location?.input)}
          classInputWithValue={twMerge(
            props.styleClasses.inputWithValue,
            props.inputClasses?.location?.inputWithValue,
            props.classes_input,
          )}
        />
      );
      break;
    case 'preferred-contact':
      FormField = (
        <CommonSelect
          name="contact"
          label="Preferred Contact"
          placeholder=""
          options={[
            { title: 'Any', value: String(DynamicsDealershipContactType.any) },
            { title: 'Email', value: String(DynamicsDealershipContactType.email) },
            { title: 'Phone', value: String(DynamicsDealershipContactType.call) },
            { title: 'SMS', value: String(DynamicsDealershipContactType.text) },
          ]}
          isRequired={props.required}
          classContainer={twMerge(props.styleClasses.container, props.inputClasses?.contact?.container, props.classes)}
          classLabel={twMerge(props.styleClasses.label, props.inputClasses?.contact?.label, props.classes_label)}
          classLabelWithValue={twMerge(
            props.styleClasses.labelWithValue,
            props.inputClasses?.contact?.labelWithValue,
            props.classes_label,
          )}
          classInput={twMerge(props.styleClasses.input, props.classes_input, props.inputClasses?.contact?.input)}
          classInputWithValue={twMerge(
            props.styleClasses.inputWithValue,
            props.inputClasses?.contact?.inputWithValue,
            props.classes_input,
          )}
        />
      );
      break;
    case 'product':
      FormField = (
        <CommonSelect
          name="product"
          label="Product"
          placeholder=""
          options={options}
          isRequired={props.required}
          classContainer={twMerge(props.styleClasses.container, props.inputClasses?.product?.container, props.classes)}
          classLabel={twMerge(props.styleClasses.label, props.inputClasses?.product?.label, props.classes_label)}
          classLabelWithValue={twMerge(
            props.styleClasses.labelWithValue,
            props.inputClasses?.product?.labelWithValue,
            props.classes_label,
          )}
          classInput={twMerge(props.styleClasses.input, props.classes_input, props.inputClasses?.product?.input)}
          classInputWithValue={twMerge(
            props.styleClasses.inputWithValue,
            props.inputClasses?.product?.inputWithValue,
            props.classes_input,
          )}
        />
      );
      break;
    case 'comments':
      FormField = (
        <CommonTextarea
          label="Comments"
          name="comments"
          isRequired={props.required}
          classContainer={twMerge(props.styleClasses.container, props.inputClasses?.comments?.container, props.classes)}
          classLabel={twMerge(props.styleClasses.label, props.inputClasses?.comments?.label, props.classes_label)}
          classLabelWithValue={twMerge(
            props.styleClasses.labelWithValue,
            props.inputClasses?.comments?.labelWithValue,
            props.classes_label,
          )}
          classInput={twMerge(props.styleClasses.input, props.classes_input, props.inputClasses?.comments?.input)}
          classInputWithValue={twMerge(
            props.styleClasses.inputWithValue,
            props.inputClasses?.comments?.inputWithValue,
            props.classes_input,
          )}
        />
      );
      break;
    default:
      FormField = null;
      break;
  }

  return (
    <div className={twMerge(props.wrapperClasses, props.extraClasses, props.type === 'comments' && 'col-span-2')}>
      {FormField}
      {props.children}
    </div>
  );
};

export const BaseTermsField = (props: IGenericInquiryFormProps) => {
  return (
    <CommonCheckbox
      name="terms"
      isRequired
      classContainer={twMerge('col-span-2 text-left items-start', props.classes)}
      className="items-start"
      classInput="border-y border-x w-4 h-4 focus:outline-none focus:ring-0"
    >
      {!props.custom_terms_text && <div dangerouslySetInnerHTML={{ __html: checkboxTerms }}></div>}
      {props.custom_terms_text && <RichText content={props.custom_terms_text} />}
    </CommonCheckbox>
  );
};

export const BaseSubscribeField = (props: IGenericInquiryFormProps) => {
  return (
    <CommonCheckbox
      name="subscribe"
      classContainer={twMerge('col-span-2 text-left items-start', props.classes)}
      className="items-start"
      classInput="border-y border-x w-4 h-4 focus:outline-none focus:ring-0"
    >
      {!props.custom_subscribe_text && <div>{checkBoxSubscribe}</div>}
      {props.custom_subscribe_text && <RichText content={props.custom_subscribe_text} />}
    </CommonCheckbox>
  );
};

// --

interface IBaseMessagesProps extends IGenericInquiryFormProps {
  wasSuccessful: boolean;
  wasError: boolean;
}

export const BaseMessages = (props: IBaseMessagesProps) => {
  return (
    <Fragment>
      {props.wasSuccessful && (
        <div className="flex flex-row items-center gap-2 my-4">
          <FontAwesomeIcon className="w-5 h-5 text-green-500 -mt-1" icon={faCheck} />
          <span className="text-black-500 font-medium">{props.message_success || messageSuccess}</span>
        </div>
      )}
      {props.wasError && (
        <div className="flex flex-row items-center gap-2 my-4">
          <FontAwesomeIcon className="w-5 h-5 text-red-700 -mt-1" icon={faXmark} />
          <span className="text-black-500 font-medium leading-none">{props.message_error || messageError}</span>
        </div>
      )}
    </Fragment>
  );
};
